// src/features/tasks/taskSlice.js - FIXED VERSION
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api';

const initialState = {
    tasks: [],
    isLoading: false,
    isSuccess: false,
    isError: false,
    message: '',
};

// Fetch user's tasks
export const fetchTasks = createAsyncThunk(
    'tasks/fetchTasks',
    async (_, thunkAPI) => {
        try {
            console.log('🔄 Fetching tasks from API...');
            const response = await apiService.getTasks();
            console.log('✅ Tasks fetched successfully:', response);
            return response;
        } catch (error) {
            console.error('❌ Failed to fetch tasks:', error);
            return thunkAPI.rejectWithValue({
                message: error.message || 'Failed to fetch tasks'
            });
        }
    }
);

// Analyze tasks
export const analyzeTasks = createAsyncThunk(
    'tasks/analyzeTasks',
    async (tasks, thunkAPI) => {
        try {
            console.log('🔄 Analyzing tasks...');
            const response = await apiService.analyzeTasks(tasks);
            console.log('✅ Tasks analyzed successfully:', response);
            return response;
        } catch (error) {
            console.error('❌ Task analysis failed:', error);
            return thunkAPI.rejectWithValue({
                message: error.message || 'Task analysis failed'
            });
        }
    }
);

// Upload CSV
export const uploadTasksCsv = createAsyncThunk(
    'tasks/uploadTasksCsv',
    async (file, thunkAPI) => {
        try {
            console.log('🔄 Uploading CSV...');
            const response = await apiService.uploadCsv(file);
            console.log('✅ CSV uploaded successfully:', response);
            return response;
        } catch (error) {
            console.error('❌ CSV upload failed:', error);
            return thunkAPI.rejectWithValue({
                message: error.message || 'CSV upload failed'
            });
        }
    }
);

// Update entire task
export const updateTask = createAsyncThunk(
    'tasks/updateTask',
    async ({ taskId, taskData }, thunkAPI) => {
        try {
            console.log(`🔄 Updating task ${taskId}...`);
            const response = await apiService.updateTask(taskId, taskData);
            console.log('✅ Task updated successfully:', response);
            return response;
        } catch (error) {
            console.error('❌ Failed to update task:', error);
            return thunkAPI.rejectWithValue({
                message: error.message || 'Failed to update task'
            });
        }
    }
);

// Update task status - THIS IS THE KEY FIX
export const updateTaskStatus = createAsyncThunk(
    'tasks/updateTaskStatus',
    async ({ taskId, status }, thunkAPI) => {
        try {
            console.log(`🔄 Redux: Updating task ${taskId} status to ${status}...`);
            const response = await apiService.updateTaskStatus(taskId, status);
            console.log('✅ Redux: Task status updated successfully:', response);
            console.log('📊 Redux: Updated task data:', { id: response.id, status: response.status });
            return response;
        } catch (error) {
            console.error('❌ Redux: Failed to update task status:', error);
            return thunkAPI.rejectWithValue({
                message: error.message || 'Failed to update task status'
            });
        }
    }
);

const taskSlice = createSlice({
    name: 'tasks',
    initialState,
    reducers: {
        addTask: (state, action) => {
            state.tasks.push(action.payload);
        },
        clearTasks: (state) => {
            state.tasks = [];
        },
        resetTaskState: (state) => {
            state.isLoading = false;
            state.isSuccess = false;
            state.isError = false;
            state.message = '';
        },
        // Local update for immediate UI response
        updateTaskLocal: (state, action) => {
            const index = state.tasks.findIndex(task => task.id === action.payload.id);
            if (index !== -1) {
                state.tasks[index] = { ...state.tasks[index], ...action.payload };
                console.log('🔄 Local task update:', state.tasks[index]);
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch tasks cases
            .addCase(fetchTasks.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(fetchTasks.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.tasks = action.payload;
                console.log('📊 Tasks stored in Redux:', action.payload);
                console.log('📊 Task statuses in Redux:', action.payload.map(t => ({ id: t.id, status: t.status })));
            })
            .addCase(fetchTasks.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload.message;
                console.error('❌ Fetch tasks error:', action.payload.message);
            })

            // Analyze tasks cases
            .addCase(analyzeTasks.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(analyzeTasks.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.tasks = action.payload;
            })
            .addCase(analyzeTasks.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload.message;
            })

            // Upload CSV cases
            .addCase(uploadTasksCsv.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(uploadTasksCsv.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.tasks = action.payload;
            })
            .addCase(uploadTasksCsv.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload.message;
            })

            // Update entire task cases
            .addCase(updateTask.pending, (state) => {
                // Don't set loading for individual task updates to avoid UI issues
                state.isError = false;
                state.message = '';
            })
            .addCase(updateTask.fulfilled, (state, action) => {
                state.isSuccess = true;
                // Update the specific task in the array
                const index = state.tasks.findIndex(task => task.id === action.payload.id);
                if (index !== -1) {
                    state.tasks[index] = action.payload;
                    console.log('✅ Task updated in Redux state:', state.tasks[index]);
                } else {
                    console.log('⚠️ Task not found in Redux state for update');
                }
            })
            .addCase(updateTask.rejected, (state, action) => {
                state.isError = true;
                state.message = action.payload.message;
            })

            // Update task status cases - CRITICAL FIX
            .addCase(updateTaskStatus.pending, (state) => {
                // Don't set loading to avoid UI flickering
                state.isError = false;
                state.message = '';
            })
            .addCase(updateTaskStatus.fulfilled, (state, action) => {
                state.isSuccess = true;

                console.log('🎯 Redux: Processing status update fulfilled');
                console.log('🎯 Redux: Updated task data:', action.payload);

                // Find and update the specific task in the array
                const index = state.tasks.findIndex(task => task.id === action.payload.id);
                if (index !== -1) {
                    // Completely replace the task with the updated version
                    state.tasks[index] = action.payload;
                    console.log('✅ Redux: Task status updated in state:', {
                        id: state.tasks[index].id,
                        status: state.tasks[index].status,
                        completed_at: state.tasks[index].completed_at
                    });
                } else {
                    console.log('⚠️ Redux: Task not found in state for status update');
                }

                // Debug: Log all task statuses after update
                const taskStatuses = state.tasks.map(t => ({ id: t.id, status: t.status }));
                console.log('📊 Redux: All task statuses after update:', taskStatuses);
            })
            .addCase(updateTaskStatus.rejected, (state, action) => {
                state.isError = true;
                state.message = action.payload.message;
                console.error('❌ Redux: Status update rejected:', action.payload.message);
            });
    },
});

export const { addTask, clearTasks, resetTaskState, updateTaskLocal } = taskSlice.actions;
export default taskSlice.reducer;